// @flow
import React from 'react'
import { connect } from 'react-redux'
import I18n from 'i18n-react'
import Modal from 'reactstrap/lib/Modal'
import ModalBody from 'reactstrap/lib/ModalBody'
import Immutable from 'seamless-immutable'
import moment from '../Core/Moment'

import { getProductState } from '../store/reducers.js'

// api
import { serverUrl } from '../Services/SuriApi'

// redux
import ProductActions, { createProductDescriptorValues } from '../Core/Redux/ProductRedux'
import UserDataActions from '../Core/Redux/UserDataRedux'
import ValidationActions from '../Core/Redux/ValidationRedux'
import QuoteActions, { INITIAL_STATE } from '../Core/Redux/QuoteRedux'
import NavigationActions from '../Core/Redux/NavigationRedux'
import FilterActions from '../Core/Redux/FilterRedux'
import GeneralActions, { ERROR_TYPES } from '../Core/Redux/GeneralRedux'
import LinkBuilderActions from '../Core/Redux/LinkBuilderRedux'
import DiscountsActions from '../Core/Redux/DiscountsRedux'
import GTMActions from '../Core/Redux/GTMRedux'

// core
import FieldValidator from '../Core/FieldValidator'
import { onValidate, onValidationErrorsChange, autofixErrors } from '../Core/ContainerFunctions'
import { getValueForPath, getValidCalculatorsForProductName } from '../Core/FilterFunctions'

// serializers
import { quoteAsyncRequestSerializer } from '../Core/Serializers/QuoteAsyncRequestSerializer'

// config
import { config } from '../Config/ConfigLoader'

// components
import { ValidatorScript, FieldForm, ErrorsPanel } from '../Components/'
import { VariablesDebugBlock, ActionDebugRow } from '../Components/_debug/'

// types
import type { PathArrayType, ProductContent } from '../Core/Types/ProductContent'
import type { Match } from '../Core/Types/Navigation'
import type { ExpandedSpecificationDTO } from '../Core/Types/Field'
import type { ValidationErrorType, ValidationErrorsType } from '../Core/Types/ValidationError'
import type { InputHelpsType } from '../Core/Types/InputHelpType'
import type { ArrayFieldFormTypes } from '../Core/Types/ArrayFieldFormType'
import type { ActiveSettingsType } from '../Core/Types/SettingsTypes'
import type { LocationType } from '../Core/Types/LocationType'
import type { TranslationType } from '../Core/Types/TranslationTypes'
import type { ResourceType, ResourcesType } from '../Core/Types/Resources'
import type { FormValueType, ValueObject } from '../Core/Types/Values'
import type { GlobalState } from '../store/reducers'
import type { AsMutable } from '../Core/Types/ImmutableTypes'
import type { InsuranceCompaniesType } from '../Core/Types/InsuranceCompanyTypes'
import type { QuoteIdDependsOnType } from '../Core/Redux/Types/QuoteStateType'
import type { QuoteAsyncRequestSerializerType } from '../Core/Serializers/QuoteAsyncRequestSerializer'
import type { QuoteFilterBlockType } from '../Core/Types/FilterTypes'
import type { QuoteRestrictionsType } from '../Core/Types/QuoteType'

type Props = {
  activeQuoteFilters: QuoteFilterBlockType,
  activeSettings: ActiveSettingsType & AsMutable<ActiveSettingsType>,
  addGtmEvent: Function,
  affiliate: string,
  arrayTypes: ArrayFieldFormTypes,
  beforeHydration: Object,
  debug: boolean,
  defaultTemplateVariables: AsMutable<ValueObject>,
  discountDependencies: Array<string>,
  discountErrors: ValidationErrorType,
  discounts: { [key: string]: any },
  discountScriptLoaded: boolean,
  discountsInit: (ExpandedSpecificationDTO) => void,
  discountValidationSig: boolean,
  enabledCalculators: Array<string>,
  fetchDiscountResources: Function,
  goToTab: string,
  insuranceCompanies: InsuranceCompaniesType,
  isBackOffice: boolean,
  linkSaveRequestSig: boolean,
  location: LocationType,
  match: Match,
  onActionFailure: (error: Object, errorType: number) => void,
  onChangeActiveTab: (string) => void,
  onChangeData: (formData: Object) => void,
  onChangeDiscountValidationSig: (boolean) => void,
  onChangeGeneralFailureError: (error: Error, key: string, data: any) => void,
  onChangeProductSpecification: (ExpandedSpecificationDTO) => void,
  onChangeShowValidationErrorPanel: (show: boolean) => void,
  onChangeValidationError: (key: string | PathArrayType, data: Array<string> | boolean) => void,
  onChangeValidationErrors: (validationErrors: ValidationErrorsType) => void,
  onChangeValidationSig: (validationSig: boolean) => void,
  onDiscountValidationErrors: (validationErrors: ValidationErrorsType) => void,
  onResetValidationErrors: () => void,
  onSetLastValidStep: (key: string, pathname: string) => void,
  onSetLastValidStepIndex: (string) => void,
  previousRequestId: string,
  productContent: ProductContent,
  productContentRequest: (product: string) => void,
  productInputHelps: InputHelpsType,
  productLinkData: Object, // TODO
  productName: string,
  productResourcesRequest: (product: string) => void,
  productTexts: TranslationType,
  quoteAsyncRequest: (string, QuoteAsyncRequestSerializerType, string, ?QuoteIdDependsOnType, string) => void,
  quoteFilters: QuoteFilterBlockType,
  quoteRestrictions: QuoteRestrictionsType,
  rehydrated: boolean,
  requestLinkId: string,
  resetDiscounts: (regex?: Object) => void,
  resetFilterData: () => void,
  resetFilterFunctionalData: () => void,
  resetQuotes: (boolean) => void,
  resources: ResourcesType,
  saveUtmParams: () => void,
  sendLinkRequestLoaderOff: (string, Object, link?: string) => void,
  setDiscountDependencies: (specification: ExpandedSpecificationDTO) => void,
  setDiscounts: (discounts: { [key: string]: any }) => void,
  setEnabledCalculators: (Array<string>) => void,
  setFilter: (tab: string, name: string, value: string, multiSelect?: boolean) => void,
  setGtmPageView: (string, string) => void,
  setLinkSaveRequestSig: (boolean) => void,
  setNumberOfItemsInArrayType: (string, string, number) => void,
  setPayment: (string) => void,
  setProductLinkTitle: (string) => void,
  setProductLinkUrlRequest: Function, // TODO
  setProperties: (Object) => null,
  setQuoteFilters: (any) => void, // TODO
  setQuoteRequestDataCalculators: (Array<string>, Array<string> | false) => void,
  setStompConnected: (boolean) => void,
  showValidationErrorPanel: boolean,
  specification: ExpandedSpecificationDTO,
  subProductName: string,
  token: string,
  updateArrayTypes: (ExpandedSpecificationDTO) => void,
  userData: { [key: string]: any },
  userDataInit: (specification: ExpandedSpecificationDTO) => void,
  userDataRemoveValue: (key: string) => void,
  userDataSetValue: (key: string | PathArrayType, value: FormValueType) => void,
  validationErrors: ValidationErrorType,
  validationSig: boolean,
  validator: FieldValidator,
  valueNames: { [key: string]: { [key: string]: string } },
  wasCicCompleted: (boolean) => void,
  webType: number,
}

type State = {
  debugModalOpened: boolean,
  discountSpecification: null | ExpandedSpecificationDTO,
  pushIsPristine: boolean,
  saveDataModalOpened: boolean,
  sendCallbackOnInactive: boolean,
  showCallback: boolean,
}

class ProductDetailContainer extends React.Component<Props, State> {
  _validator: FieldValidator
  _scriptLoaded: boolean = false
  _validateTimer: TimeoutID

  state = {
    debugModalOpened: false,
    discountSpecification: null,
    pushIsPristine: true,
    saveDataModalOpened: false,
    sendCallbackOnInactive: false,
    showCallback: false,
  }

  componentDidMount() {
    const {
      insuranceCompanies,
      enabledCalculators,
      productName,
      quoteRestrictions,
      isBackOffice,
      userData,
      location,
    } = this.props
    const { setEnabledCalculators, onResetValidationErrors, setGtmPageView } = this.props
    this.handleHideErrorPanel()
    onResetValidationErrors()
    if (enabledCalculators.length === 0 || !isBackOffice) {
      setEnabledCalculators(
        getValidCalculatorsForProductName(insuranceCompanies, productName, quoteRestrictions, isBackOffice),
      )
    }
    if (!isBackOffice || !userData.getIn(['paymentPeriod'], false)) {
      this.handleOnChangePayment('annually')
    }

    setGtmPageView(location.pathname, document.title)
    this.handleScriptLoad()
    if (this.props.rehydrated) {
      this.handleCheckData()
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    // not in render discountDependencies
    // not in render quoteRequests
    // not in render validator
    const should =
      nextProps.showValidationErrorPanel !== this.props.showValidationErrorPanel ||
      nextProps.discountScriptLoaded !== this.props.discountScriptLoaded ||
      nextProps.validationSig !== this.props.validationSig ||
      nextProps.discountValidationSig !== this.props.discountValidationSig ||
      nextProps.rehydrated !== this.props.rehydrated ||
      JSON.stringify([
        nextProps.userData,
        nextProps.specification,
        nextProps.discounts,
        nextProps.arrayTypes,
        nextProps.discountErrors,
        nextProps.validationErrors,
        nextState.discountSpecification,
      ]) !==
        JSON.stringify([
          this.props.userData,
          this.props.specification,
          this.props.discounts,
          this.props.arrayTypes,
          this.props.discountErrors,
          this.props.validationErrors,
          this.state.discountSpecification,
        ]) ||
      nextState.pushIsPristine !== this.state.pushIsPristine ||
      nextState.sendCallbackOnInactive !== this.state.sendCallbackOnInactive ||
      nextState.showCallback !== this.state.showCallback ||
      (nextProps.isBackOffice &&
        (nextProps.goToTab !== this.props.goToTab ||
          JSON.stringify([
            nextProps.quoteFilters,
            nextProps.isBackOffice,
            nextProps.activeQuoteFilters,
            nextProps.productLinkData,
            nextProps.quoteRestrictions,
          ]) !==
            JSON.stringify([
              this.props.quoteFilters,
              this.props.isBackOffice,
              this.props.activeQuoteFilters,
              this.props.productLinkData,
              this.props.quoteRestrictions,
            ]) ||
          nextState.saveDataModalOpened !== this.state.saveDataModalOpened)) ||
      nextProps.productName !== this.props.productName ||
      nextProps.subProductName !== this.props.subProductName ||
      nextProps.webType !== this.props.webType ||
      nextProps.token !== this.props.token ||
      nextProps.debug !== this.props.debug ||
      nextProps.affiliate !== this.props.affiliate ||
      JSON.stringify([
        nextProps.valueNames,
        nextProps.productTexts,
        nextProps.enabledCalculators,
        nextProps.productContent,
        nextProps.productInputHelps,
        nextProps.insuranceCompanies,
        nextProps.defaultTemplateVariables,
        nextProps.activeSettings,
        nextProps.resources,
      ]) !==
        JSON.stringify([
          this.props.valueNames,
          this.props.productTexts,
          this.props.enabledCalculators,
          this.props.productContent,
          this.props.productInputHelps,
          this.props.insuranceCompanies,
          this.props.defaultTemplateVariables,
          this.props.activeSettings,
          this.props.resources,
        ]) ||
      (nextProps.debug && nextState.debugModalOpened !== this.state.debugModalOpened)
    return should
  }

  componentDidUpdate(prevProps: Props) {
    const { validationSig } = this.props
    const { onChangeValidationSig } = this.props
    if (validationSig) {
      onChangeValidationSig(false)
      this.handleValidate()
    }
    if (this.props.rehydrated !== prevProps.rehydrated && this._validator) {
      this.handleCheckData()
      this.props.userDataInit(this._validator.getActualSpecification())
    }
  }

  handleCheckData() {
    const insuranceStart = this.props.userData.getIn(['trip', 'insuranceStart'], false)
    if (insuranceStart) {
      if (moment().isSame(moment(insuranceStart), 'day')) {
        this.handleChangeValue(
          'insuranceStart',
          moment()
            .endOf('day')
            .subtract(40, 'minutes')
            .format('YYYY-MM-DDTHH:mm'),
          ['trip', 'insuranceStart'],
        )
      }
    }
  }

  handleScriptCreate = () => {}

  handleScriptError = (e: Object) => {
    this.props.onActionFailure(e, ERROR_TYPES.CRITICAL)
  }

  handleScriptLoad = (validate: boolean = false) => {
    if (window['Validator'] && !this._scriptLoaded) {
      this._scriptLoaded = true
      const { userDataInit } = this.props

      this._validator = new FieldValidator(() => {}, this.handleFetchResource)

      userDataInit(this._validator.getActualSpecification())
      if (validate) {
        this.handleValidate()
      }
    }
  }

  handleGetDiscountsForRequest = (discounts?: { [key: string]: any }) => {
    const { userData, specification, arrayTypes } = this.props
    const { discountSpecification } = this.state

    if (!discountSpecification) {
      return {}
    }

    const discountsToValidate = discounts || this.props.discounts
    return {
      ...createProductDescriptorValues(
        discountsToValidate,
        discountSpecification,
        {},
        true,
        undefined,
        new RegExp('^(?!equipment)', 'g'),
      ),
      values: createProductDescriptorValues(userData, specification, arrayTypes, true),
      selectedTariffs: {},
    }
  }

  handleFetchResource = (resourceName: string): void | ResourceType => {
    const { resources } = this.props
    const resource = getValueForPath(resourceName, 0, resources)
    if (resource) {
      return resource
    }
  }

  handleChangeValue = (key: string, value: FormValueType, pathArray: PathArrayType) => {
    const { onChangeValidationError, userDataSetValue, onSetLastValidStepIndex } = this.props
    if (pathArray[0] === 'trip' && pathArray[1] === 'recreationalSports' && !value) {
      return
    }
    if (pathArray.includes('insuredParties')) {
      this.props.userDataSetValueInsuredParties(pathArray, value)
    } else {
      userDataSetValue(pathArray, value)
    }
    onSetLastValidStepIndex('product')
    const { errors } = this.handleValidate(pathArray, value)
    const error = getValueForPath(pathArray, 0, errors)
    onChangeValidationError(pathArray, error ? error : false)
  }

  handleChangeFormData = (formData: Object) => {
    const { onChangeData, onSetLastValidStepIndex, onChangeValidationSig } = this.props
    onChangeData(formData)
    onSetLastValidStepIndex('product')
    onChangeValidationSig(true)
  }

  handleValidate = (
    pathArray?: PathArrayType,
    value?: FormValueType,
    userData?: Object,
    pushSaveErrors: boolean = false,
    removeDisabled: boolean = true,
  ) => {
    const { specification, arrayTypes, onChangeValidationErrors } = this.props
    if (this._validator && specification && specification.fields) {
      const userDataToValidate = userData || this.props.userData
      let productDescriptorValues = createProductDescriptorValues(
        userDataToValidate,
        specification,
        arrayTypes,
        removeDisabled,
      )

      if (Array.isArray(pathArray) && typeof value !== 'undefined') {
        productDescriptorValues = new Immutable(productDescriptorValues).setIn(pathArray, value).asMutable()
      }

      let { isValid, errors } = onValidate(
        productDescriptorValues,
        pathArray,
        this._validator,
        this.handleChangeProductSpecification,
        userDataToValidate,
        specification,
      )

      if (pushSaveErrors || !this.state.pushIsPristine) {
        onChangeValidationErrors(onValidationErrorsChange(errors, userDataToValidate))
      }

      return { isValid, errors }
    } else {
      if (this._validateTimer) {
        clearTimeout(this._validateTimer)
      }
      this._validateTimer = setTimeout(() => {
        this.handleScriptLoad(true)
      }, 1500)
    }
    return { isValid: false, errors: {} }
  }

  handleChangeProductSpecification = (specification: ExpandedSpecificationDTO) => {
    this.props.updateArrayTypes(specification)
    this.props.onChangeProductSpecification(specification)
  }

  handleOnChangePayment = (payment: string) => {
    this.props.setPayment(payment)
  }

  handleSubmit = () => {
    const { specification, userData, arrayTypes, productName, isBackOffice } = this.props
    const {
      resetQuotes,
      quoteAsyncRequest,
      onChangeShowValidationErrorPanel,
      onChangeValidationErrors,
      wasCicCompleted,
      onChangeActiveTab,
      subProductName,
      resetFilterData,
      resetDiscounts,
      setStompConnected,
      onResetValidationErrors,
      saveUtmParams,
    } = this.props

    onResetValidationErrors()
    saveUtmParams()

    if (specification) {
      this.setState({ pushIsPristine: false })
      let productDescriptorValues = createProductDescriptorValues(userData, specification, arrayTypes)
      let validator = this.handleValidate(undefined, undefined, productDescriptorValues)

      if (!validator.isValid) {
        productDescriptorValues = autofixErrors(validator.errors, productDescriptorValues, specification)
        validator = this.handleValidate(null, null, productDescriptorValues)
        if (validator.isValid) {
          this.props.onChangeData(productDescriptorValues)
        }
      }
      let { isValid, errors } = validator
      if (isValid) {
        this.handleHideErrorPanel()
        resetQuotes(isBackOffice)
        // $FlowIssue
        wasCicCompleted(!!productDescriptorValues.cic)
        let goToTab = config.getConfig('productDetailContainer.quotesKey')
        if (this.props.goToTab) {
          goToTab = this.props.goToTab
        } else if (userData.cicEnabled && subProductName === 'cic' && !isBackOffice) {
          goToTab = 'cic'
        } else if (goToTab === 'tplCic' && !userData.cicEnabled) {
          goToTab = 'tpl'
        }
        const quoteRequestData = INITIAL_STATE.getIn(['quoteRequests', goToTab, 'quoteRequestData'], {})
        resetFilterData()
        if (!isBackOffice) {
          resetDiscounts(RegExp('commercial*'))
        }
        onChangeActiveTab(goToTab)
        setStompConnected(false)
        const asyncData = quoteAsyncRequestSerializer(
          this.props,
          productDescriptorValues,
          quoteRequestData,
          this.handleGetDiscountsForRequest(),
          'quoteAsyncRequestSerializer',
          ['TravelSlaviaOnline', ...(userData.getIn(['trip', 'insuranceType']) !== 'repeated-trips' ? ['TravelAXA'] : [])],
          config.getConfig('quoteListContainer.useFiltersForQuotes').includes(goToTab),
        )
        quoteAsyncRequest(productName, asyncData, goToTab, undefined, subProductName)
      } else {
        onChangeShowValidationErrorPanel(true)
        onChangeValidationErrors(onValidationErrorsChange(errors, userData))
      }
    }
  }

  handleSetNumberOfItemsInArrayType = (key: string, path: string, value: number) => {
    this.props.setNumberOfItemsInArrayType(key, path, value)
  }

  handleToggleDebugModal = () => {
    this.setState({ debugModalOpened: !this.state.debugModalOpened })
  }

  handleHideErrorPanel = () => {
    this.props.onChangeShowValidationErrorPanel(false)
  }

  render() {
    const {
      specification,
      defaultTemplateVariables,
      activeSettings,
      debug,
      productContent,
      validationErrors,
      showValidationErrorPanel,
      productTexts,
      arrayTypes,
      userData,
      productInputHelps,
      resources,
      valueNames,
      productName,
      isBackOffice,
      token,
      webType,
    } = this.props

    const templateVariables = {
      ...defaultTemplateVariables,
      ...activeSettings,
      email: I18n.translate('acceptScreen.contactEmail', { context: webType }),
      phoneNumber: I18n.translate('acceptScreen.phoneNumber', { context: webType }),
    }

    return (
      <div>
        <ActionDebugRow
          active={debug}
          buttonActions={[this.handleToggleDebugModal]}
          buttonTexts={['Zobrazit dostupné proměnné v šablonách']}
        />
        {productContent && productContent.fieldOrder && specification && specification.fields && (
          <div>
            <FieldForm
              activeSettings={activeSettings}
              arrayTypes={arrayTypes}
              blockClassName="card card-lg mx-auto bg-transparent border-0 border-sm-default mt-2 mt-sm-5"
              blockClassNameForR="card card-lg mx-auto bg-transparent border-0 border-sm-default mt-2 mt-sm-5"
              blockContentClassName="card-body px-0 px-sm-3"
              content={productContent}
              data={userData}
              debug={debug}
              formData={userData}
              inputHelps={productInputHelps}
              isBackOffice={isBackOffice}
              key="form_root"
              onChangeFormData={this.handleChangeFormData}
              onChangeValue={this.handleChangeValue}
              pushIsPristine={this.state.pushIsPristine}
              resources={resources}
              setNumberOfItemsInArrayType={this.handleSetNumberOfItemsInArrayType}
              showArrayTypeInput
              specification={specification}
              templateVariables={templateVariables}
              token={token}
              validationErrors={validationErrors}
              valueNames={valueNames}
            />
            {!isBackOffice && (
              <div className="text-center mt-1 mt-md-4 py-2">
                <span>
                  {I18n.translate('general.agreeWithTermsAndConditions', {
                    link: (
                      <a
                        href={`${activeSettings.domain}informace-o-zpracovani-osobnich-udaju/`}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {I18n.translate('general.infoAboutDataProcessing')}
                      </a>
                    ),
                  })}
                </span>
              </div>
            )}
            <div className="text-center mt-1 mt-md-4 py-2 mb-5">
              <button className="btn btn-secondary btn-lg" onClick={this.handleSubmit} type="button">
                {I18n.translate('submit.step1')}
              </button>
            </div>
          </div>
        )}
        <ValidatorScript
          handleScriptCreate={this.handleScriptCreate}
          handleScriptError={this.handleScriptError}
          handleScriptLoad={() => {
            this.handleScriptLoad(true)
          }}
          id="product-validator"
          url={`${serverUrl}/product/${productName}/validator.js?access_token=${token}`}
        />
        <ErrorsPanel
          debug={debug}
          errors={validationErrors}
          isVisible={showValidationErrorPanel}
          onHide={this.handleHideErrorPanel}
          texts={productTexts}
        />
        {debug && (
          <Modal isOpen={this.state.debugModalOpened} toggle={this.handleToggleDebugModal}>
            <ModalBody>
              <VariablesDebugBlock
                active={debug}
                collapseObjects
                showOnlyTypes
                title="templateVariables"
                variables={templateVariables}
              />
            </ModalBody>
          </Modal>
        )}
      </div>
    )
  }
}

const mapStateToProps = (oldState) =>
  getProductState(oldState, (state: GlobalState) => ({
    activeQuoteFilters: state.quote.filters,
    activeSettings: state.settings.activeSettings,
    affiliate: state.product.affiliate,
    arrayTypes: state.userData.arrayTypes,
    debug: state.debug.debug,
    defaultTemplateVariables: state.inputHelps.defaultTemplateVariables,
    discountDependencies: state.discounts.discountDependencies,
    discountErrors: state.validation.discountErrors,
    discounts: state.discounts.discounts,
    discountValidationSig: state.validation.discountValidationSig,
    enabledCalculators: state.filter.enabledCalculators,
    goToTab: state.quote.goToTab,
    insuranceCompanies: state.general.insuranceCompanies,
    isBackOffice: config.getConfig('isBackOffice'),
    linkSaveRequestSig: state.linkBuilder.linkSaveRequestSig,
    previousRequestId: state.userData.previousRequestId,
    productContent: state.product.productContent,
    productInputHelps: state.inputHelps.productInputHelps,
    productLinkData: state.linkBuilder.productLinkData,
    productName: state.product.productName,
    productTexts: state.product.productTexts,
    quoteFilters: state.product.quoteFilters,
    quoteRequests: state.quote.quoteRequests,
    quoteRestrictions: state.quote.quoteRestrictions,
    requestLinkId: state.linkBuilder.requestLinkId,
    resources: state.product.resources,
    showValidationErrorPanel: state.validation.showValidationErrorPanel,
    specification: state.product.specification,
    subProductName: state.product.subProductName,
    userData: state.userData.data,
    validationErrors: state.validation.validationErrors,
    validationSig: state.validation.validationSig,
    valueNames: {
      manufacturer: state.product.transManufacturer,
      model: state.product.transModel,
    },
    webType: state.settings.webType,
  }))

const mapDispatchToProps = {
  addGtmEvent: GTMActions.addGtmEvent,
  discountsInit: DiscountsActions.discountsInit,
  onActionFailure: GeneralActions.onActionFailure,
  onAddStep: NavigationActions.onAddStep,
  onChangeActiveTab: QuoteActions.onChangeActiveTab,
  onChangeData: UserDataActions.onChangeData,
  onChangeDiscountValidationSig: ValidationActions.onChangeDiscountValidationSig,
  onChangeGeneralFailureError: ValidationActions.onChangeGeneralFailureError,
  onChangeProductSpecification: ProductActions.onChangeProductSpecification,
  onChangeShowValidationErrorPanel: ValidationActions.onChangeShowValidationErrorPanel,
  onChangeValidationError: ValidationActions.onChangeValidationError,
  onChangeValidationErrors: ValidationActions.onChangeValidationErrors,
  onChangeValidationSig: ValidationActions.onChangeValidationSig,
  onResetValidationErrors: ValidationActions.onResetValidationErrors,
  onSetLastValidStep: NavigationActions.onSetLastValidStep,
  onSetLastValidStepIndex: NavigationActions.onSetLastValidStepIndex,
  quoteAsyncRequest: QuoteActions.quoteAsyncRequest,
  resetDiscounts: DiscountsActions.resetDiscounts,
  resetFilterData: FilterActions.resetFilterData,
  resetFilterFunctionalData: FilterActions.resetFilterFunctionalData,
  resetQuotes: QuoteActions.resetQuotes,
  saveGenLinkDataRequest: LinkBuilderActions.saveGenLinkDataRequest,
  sendLinkRequestLoaderOff: LinkBuilderActions.sendLinkRequestLoaderOff,
  setDiscountDependencies: DiscountsActions.setDiscountDependencies,
  setDiscounts: DiscountsActions.setDiscounts,
  setEnabledCalculators: FilterActions.setEnabledCalculators,
  setFilter: QuoteActions.setFilter,
  setGtmPageView: GTMActions.setGtmPageView,
  setLinkSaveRequestSig: LinkBuilderActions.setLinkSaveRequestSig,
  setNumberOfItemsInArrayType: UserDataActions.setNumberOfItemsInArrayType,
  setPayment: UserDataActions.setPayment,
  setProductLinkTitle: LinkBuilderActions.setProductLinkTitle,
  setProductLinkUrlRequest: LinkBuilderActions.setProductLinkUrlRequest,
  setProperties: UserDataActions.setProperties,
  setQuoteFilters: QuoteActions.setQuoteFilters,
  setQuoteRequestDataCalculators: QuoteActions.setQuoteRequestDataCalculators,
  setStompConnected: QuoteActions.setStompConnected,
  updateArrayTypes: UserDataActions.updateArrayTypes,
  userDataInit: UserDataActions.userDataInit,
  userDataRemoveValue: UserDataActions.userDataRemoveValue,
  userDataSetValue: UserDataActions.userDataSetValue,
  userDataSetValueInsuredParties: UserDataActions.userDataSetValueInsuredParties,
  wasCicCompleted: UserDataActions.wasCicCompleted,
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductDetailContainer)
